import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Goal from "./Goal";
import BucketAction from "../actions/BucketAction";
import ProjectAction from "../actions/ProjectAction";
import GoalAction from "../actions/GoalAction";
import {
  Bars4Icon,
  BuildingOffice2Icon,
  CalendarDaysIcon,
  ClockIcon,
  CurrencyDollarIcon,
  UserCircleIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import { GoalIcon } from "../AppNav";
import { format, formatDistance, isBefore, isValid } from "date-fns";
import { fromJS } from "immutable";
import { formatAmount } from "../api/DataTypeUtils";
import GoalEmptyState from "./GoalEmptyState";
import { useTranslation } from "react-i18next";

export const getScopeLabel = (goal, t) => {
  if (goal.getIn(["scope", "type"]) == "Team") {
    return t("Team");
  }
  return `${t(goal.getIn(["scope", "type"]))} - ${goal.getIn(["scope", "value", "label"])}`;
};

export const getGoalSummary = (goal, currency, t, i18n) => {
  return `${goal.get("frequency")} - ${getScopeLabel(goal, t)} - ${getGoalLabel(goal, currency, i18n)}`;
};

export const GoalScope = ({ goal }) => {
  const { t } = useTranslation();
  if (goal.getIn(["scope", "type"]) == "Team") {
    return (
      <>
        <div className={"w-6"}>
          <UserGroupIcon className={"w-6"} />
        </div>
        <div>{t("Team")}</div>
      </>
    );
  }
  if (goal.getIn(["scope", "type"]) == "Account") {
    return (
      <>
        <div className={"w-6"}>
          <BuildingOffice2Icon className={"w-6"} />
        </div>
        <div>{goal.getIn(["scope", "value", "label"])}</div>
      </>
    );
  }
  return (
    <>
      <div className={"w-6"}>
        <UserCircleIcon className={"w-6"} />
      </div>
      <div>{goal.getIn(["scope", "value", "label"]) || t("No Assignee")}</div>
    </>
  );
};

export const GoalType = ({ goal }) => {
  const goalTypes = useSelector((state: any) => state.goal.get("goalTypes"));
  const goalType = goalTypes.find((item) => item.get("value") == goal.get("type")) || fromJS({});
  return (
    <>
      <div className={"w-6"}>
        <CurrencyDollarIcon className={"w-6"} />
      </div>
      <div>{goalType.getIn(["label"])}</div>
    </>
  );
};

export const GoalValueInfo = ({ goal }) => {
  const { i18n } = useTranslation();
  const currency = useSelector((state: any) => state.projectReducer.getIn(["project", "currency"]));
  return (
    <>
      <div className={"w-6"}>
        <GoalIcon />
      </div>
      <div>{getGoalLabel(goal, currency, i18n)}</div>
    </>
  );
};

export const getGoalLabel = (goal, currency, i18n) => {
  if (goal.get("trackingMetric") == "Count") {
    return `${goal.getIn(["trackingGoal", "value"])} deals`;
  }
  return formatAmount(i18n.language, goal.getIn(["trackingGoal", "value"]), currency);
};

export const GoalInfo = ({ goal }) => {
  const navigate = useNavigate();
  const { projectId }: any = useParams();
  const renderFrequency = (goal) => {
    const dateFrom = new Date(goal.getIn(["trackingFrom"]));
    const dateTo = new Date(goal.getIn(["trackingTo"]));
    let dateFromStr = "";
    if (isValid(dateFrom)) {
      dateFromStr = format(dateFrom, "MM/dd/yyyy");
    }
    if (isValid(dateTo)) {
      dateFromStr += " - " + format(dateTo, "MM/dd/yyyy");
    } else {
      dateFromStr += " - No End Date";
    }
    return <div>{dateFromStr}</div>;
  };

  const showGoalDetail = (goal) => {
    const url = `/workspaces/${projectId}/settings/goals/${goal.get("id")}?key=Goals`;
    navigate(url);
  };

  const renderBuckets = () => {
    if (goal.get("buckets").size == 0) {
      return <div>No Pipelines</div>;
    }
    return (
      <ul className={"grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-1 lg:grid-cols-2"}>
        {goal.get("buckets").map((bucket) => (
          <li
            title={bucket.get("label")}
            className={
              "h-7 bg-gray-200 rounded-sm text-blue-900 px-2 col-span-1 flex shadow-xs truncate ..."
            }
            key={`buck_${bucket.get("value")}`}
          >
            {bucket.get("label")}
          </li>
        ))}
      </ul>
    );
  };

  const renderGoalInfo = () => {
    return (
      <div
        className={"w-full h-full px-4 overflow-y-auto"}
        onClick={() => {
          showGoalDetail(goal);
        }}
      >
        <div className="space-y-2">
          <div className={"flex space-x-2"}>
            <GoalScope goal={goal} />
          </div>
          <div className={"flex space-x-2"}>
            <GoalType goal={goal} />
          </div>
          <div className={"flex space-x-2"}>
            <div className={"w-6"}>
              <Bars4Icon className={"w-6"} />
            </div>
            {renderBuckets()}
          </div>
        </div>

        <div className={"space-y-2"}>
          <div className={"flex space-x-2"}>
            <div className={"w-6"}>
              <CalendarDaysIcon className={"w-6"} />
            </div>
            <div>{goal.getIn(["frequency"])}</div>
          </div>
          <div className={"flex space-x-2"}>
            <div className={"w-6"}>
              <ClockIcon className={"w-6"} />
            </div>
            {renderFrequency(goal)}
          </div>
          <div className={"flex space-x-2"}>
            <GoalValueInfo goal={goal} />
          </div>
        </div>
      </div>
    );
  };
  return renderGoalInfo();
};

export const isActiveGoal = (goal) => {
  const dateTo = new Date(goal.get("trackingTo"));
  if (isValid(dateTo) && isBefore(dateTo, new Date())) {
    return false;
  }
  return true;
};

export const GoalStatus = ({ goal }) => {
  if (!isActiveGoal(goal)) {
    return (
      <span className="inline-flex items-center rounded-full bg-gray-50 px-2 py-1 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10">
        Past
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
      Active
    </span>
  );
};

export const GoalList = () => {
  const dispatch = useDispatch();
  const { projectId, goalId }: any = useParams();
  const goals = useSelector((state: any) => state.goal.get("goals"));

  const navigate = useNavigate();

  useEffect(() => {
    document.title = `Goals`;
    dispatch(ProjectAction.getProject(projectId));
    dispatch(BucketAction.getBuckets(projectId, "SALE"));
    dispatch(GoalAction.getGoals(projectId));
  }, [dispatch, projectId]);

  const formatDate = (dateArray: any) => {
    const dateObject = new Date(dateArray);
    return formatDistance(dateObject, Date.now());
  };

  const renderGoals = () => {
    return (
      <div className={"w-full h-full px-4"}>
        <ul role="list" className="divide-y divide-gray-100 space-y-2">
          {goals.map((goal) => (
            <li key={goal.get("id")} className="flex justify-between gap-x-6 py-5 shadow-sm">
              <GoalInfo goal={goal} />
              <div className="hidden shrink-0 sm:flex sm:flex-col sm:items-end pr-4">
                <div>
                  <GoalStatus goal={goal} />
                </div>
                <div className={"flex space-x-2"}>
                  <div>{goal.getIn(["createdByUser", "displayName"])}</div>
                  <div>-</div>
                  <div>Updated {formatDate(goal.getIn(["updatedAt"]))} ago</div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const renderView = () => {
    if (goalId) {
      return renderGoalDetail();
    }
    if (goals.size == 0) {
      return renderEmptyState();
    }
    return renderGoals();
  };

  const renderGoalDetail = () => {
    return (
      <Goal
        projectId={projectId}
        goalId={goalId}
        onClose={() => {
          handleGoBack();
        }}
      />
    );
  };

  const renderEmptyState = () => {
    return <GoalEmptyState />;
  };

  const handleGoBack = () => {
    const url = `/workspaces/${projectId}/settings/goals?key=Goals`;
    navigate(url);
  };

  return <div className={"p-4"}>{renderView()}</div>;
};

export default GoalList;
